/* eslint-disable import/prefer-default-export */
import { gql } from 'graphql-request';

export const listDrugProfilesGql = gql`
  query listDrugProfiles(
    $limit: Int = 25
    $offset: Int = 0
    $filter: DrugProfileFilter
    $sortBy: DrugProfileSortBy
  ) {
    listDrugProfiles(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      result {
        drugName
        strength
        unit
        ndc
        manufacturer
        createdAt
        updatedAt
        isActive
        packageSize
        imprint
        drugProfileId
        genericDescription
        shipmentInternalNote
        form
        drugNameHash
        associatedPrescriptionDescriptions
      }
    }
  }
`;

export const upsertDrugProfileGql = gql`
  mutation upsertDrugProfile($input: UpsertDrugProfileInput) {
    upsertDrugProfile(input: $input) {
      drugName
      strength
      unit
      ndc
      manufacturer
      isActive
      packageSize
      imprint
      drugProfileId
      associatedPrescriptionDescriptions
      genericDescription
      shipmentInternalNote
      form
    }
  }
`;

export const listDrugProfilesByDrugNameGql = gql`
  query listDrugProfilesByDrugName($drugName: String!, $filter: DrugProfileByDrugNameFilter) {
    listDrugProfilesByDrugName(drugName: $drugName, filter: $filter) {
      drugName
      drugProfileId
      associatedPrescriptionDescriptions
      strength
      form
      unit
    }
  }
`;

export const drugProfilesByNdcGql = gql`
  query drugProfilesByNdc($ndc: String!) {
    drugProfilesByNdc(ndc: $ndc) {
      drugName
      ndc
    }
  }
`;

export const getCatalogDrugProfileByNdcGql = gql`
  query getCatalogDrugProfileByNdc($ndc: String!) {
    getCatalogDrugProfileByNdc(ndc: $ndc) {
      count
      results {
        catalogId
        drugProfileId
        ndc
        isActive
        isDeleted
        createdTimestamp
        modifiedTimestamp
      }
    }
  }
`;

export const createCatalogDrugGql = gql`
  mutation createCatalogDrug($input: CreateCatalogDrugInput!) {
    createCatalogDrug(input: $input)
  }
`;

// The drug name <-> 3-character code registry. The Add to Catalog dialog fetches it
// once on open and then resolves a typed drug name to its code -- and picks a free
// code for a drug that has none yet -- entirely locally, so typing costs no round
// trip. Replaces listTakenDrugCodes, which only knew which codes were in use and
// so could never give a known drug its own code back.
export const listDrugCodeMappingsGql = gql`
  query listDrugCodeMappings {
    listDrugCodeMappings {
      drugName
      drugCode
    }
  }
`;

// The Form dropdown and, for each form, the pack size units it may use. The Add
// to Catalog dialog fetches this once on open, so selecting a form filters the
// Unit dropdown with no further round trip. Replaces the two hardcoded arrays
// that used to live in the dialog and had to be kept in step by hand with
// e-scribe-ui and smartphos-core.
export const listCatalogFormOptionsGql = gql`
  query listCatalogFormOptions {
    listCatalogFormOptions {
      formAlias
      shortCode
      units
    }
  }
`;

export const listCatalogGql = gql`
  query listCatalog(
    $limit: Int
    $offset: Int
    $filter: ListCatalogFilter
    $sortBy: ListCatalogSortBy
  ) {
    listCatalog(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      results {
        drugName
        variants {
          catalogId
          productId
          drugCode
          strength
          isActive
          isAvailable
          isPartnerActive
          isCompoundingEligible
          whitelisted
          isRelayEligible
          displayName
          packSizeValue
          packSizeUnit
          dosageForm
          formAlias
        }
      }
    }
  }
`;

export const updateCatalogDrugGql = gql`
  mutation updateCatalogDrug($input: UpdateCatalogDrugInput!) {
    updateCatalogDrug(input: $input)
  }
`;

export const markAvailableGql = gql`
  mutation markAvailable($input: MarkAvailableInput!) {
    markAvailable(input: $input)
  }
`;

// POM-5705: catalog products offered in the "Associate to Catalog" reviewer popup.
export const listCatalogProductsByDrugNameGql = gql`
  query listCatalogProductsByDrugName($drugName: String!, $strength: String) {
    listCatalogProductsByDrugName(drugName: $drugName, strength: $strength) {
      catalogId
      productId
      displayName
      drugName
      strength
      packSizeValue
      packSizeUnit
      dosageForm
      formAlias
      hasNdc
      isCatalogEnabled
    }
  }
`;

export const listCatalogNdcsByProductIdGql = gql`
  query listCatalogNdcsByProductId($productId: [String!]!) {
    listCatalogNdcsByProductId(productId: $productId) {
      ndc
      manufacturer
      isActive
      productId
    }
  }
`;
